import React, { useState } from 'react';
import { Search, X, Menu } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from './Logo';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  
  return (
    <>
      <header className="border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3.5 sm:py-4">
          <div className="flex items-center justify-between">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center space-x-8"
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="cursor-pointer"
              onClick={() => navigate('/')}
            >
              <Logo size={52} className="transition-transform" />
            </motion.div>
            
            <nav className="hidden sm:flex items-center space-x-6">
              <a 
                href="/tradfi" 
                className={`text-sm transition-colors ${
                  location.pathname.startsWith('/tradfi') ? 'text-white' : 'text-zinc-400 hover:text-white'
                }`}
              >
                TradFi
              </a>
              <a 
                href="/crypto" 
                className={`text-sm transition-colors ${
                  location.pathname === '/crypto' || location.pathname === '/' ? 'text-white' : 'text-zinc-400 hover:text-white'
                }`}
              >
                Crypto
              </a>
              <a 
                href="/exchanges" 
                className={`text-sm transition-colors ${
                  location.pathname === '/exchanges' ? 'text-white' : 'text-zinc-400 hover:text-white'
                }`}
              >
                Exchanges
              </a>
              <a 
                href="/portfolio" 
                className={`text-sm transition-colors ${
                  location.pathname === '/portfolio' ? 'text-white' : 'text-zinc-400 hover:text-white'
                }`}
              >
                Portfolio
              </a>
            </nav>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center space-x-2 sm:space-x-4"
          >
            <div className="relative group hidden sm:block">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-500 w-4 h-4" />
              <input
                type="text"
                placeholder="Search..."
                className="pl-10 pr-4 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-sm 
                         focus:outline-none focus:border-violet-500 transition-colors w-32 md:w-64
                         placeholder-zinc-600"
              />
            </div>
            <div className="flex items-center space-x-1 sm:hidden">
              <button 
                onClick={() => setShowMobileSearch(true)}
                className="p-2 text-zinc-400 hover:text-white transition-colors"
                aria-label="Search"
              >
                <Search className="w-5 h-5" />
              </button>
              <button 
                onClick={() => setShowMobileMenu(true)}
                className="p-2 text-zinc-400 hover:text-white transition-colors"
                aria-label="Menu"
              >
                <Menu className="w-5 h-5" />
              </button>
            </div>
          </motion.div>
          </div>
        </div>
      </header>
      
      {/* Mobile Search Overlay */}
      <AnimatePresence>
        {showMobileSearch && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="sm:hidden fixed inset-0 z-50 bg-black/95 backdrop-blur-sm"
          >
            <div className="p-4">
              <div className="flex items-center space-x-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-500 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search cryptocurrencies..."
                    className="w-full pl-12 pr-4 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-base
                             focus:outline-none focus:border-violet-500 transition-colors"
                    autoFocus
                  />
                </div>
                <button 
                  onClick={() => setShowMobileSearch(false)}
                  className="p-3 text-zinc-400 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="mt-4 text-sm text-zinc-500">
                <p>Popular searches: BTC, ETH, SOL, DOGE</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {showMobileMenu && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="sm:hidden fixed inset-0 z-50 bg-black/95 backdrop-blur-sm"
          >
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between p-4 border-b border-zinc-800">
                <Logo size={52} />
                <button 
                  onClick={() => setShowMobileMenu(false)}
                  className="p-2 text-zinc-400 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <nav className="flex-1 p-4">
                <div className="space-y-1">
                  <a 
                    href="/tradfi"
                    onClick={() => setShowMobileMenu(false)}
                    className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                      location.pathname.startsWith('/tradfi')
                        ? 'text-white bg-zinc-900' 
                        : 'text-zinc-400 hover:text-white hover:bg-zinc-900/50'
                    }`}
                  >
                    TradFi
                  </a>
                  <a 
                    href="/crypto"
                    onClick={() => setShowMobileMenu(false)}
                    className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                      location.pathname === '/crypto' || location.pathname === '/'
                        ? 'text-white bg-zinc-900' 
                        : 'text-zinc-400 hover:text-white hover:bg-zinc-900/50'
                    }`}
                  >
                    Crypto
                  </a>
                  <a 
                    href="/exchanges"
                    onClick={() => setShowMobileMenu(false)}
                    className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                      location.pathname === '/exchanges' 
                        ? 'text-white bg-zinc-900' 
                        : 'text-zinc-400 hover:text-white hover:bg-zinc-900/50'
                    }`}
                  >
                    Exchanges
                  </a>
                  <a 
                    href="/portfolio"
                    onClick={() => setShowMobileMenu(false)}
                    className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                      location.pathname === '/portfolio' 
                        ? 'text-white bg-zinc-900' 
                        : 'text-zinc-400 hover:text-white hover:bg-zinc-900/50'
                    }`}
                  >
                    Portfolio
                  </a>
                </div>
              </nav>
              
              <div className="p-4 border-t border-zinc-800">
                <p className="text-xs text-zinc-500 text-center">
                  Forged by{' '}
                  <a 
                    href="https://x.com/OGNodeFather" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-violet-400 hover:text-violet-300 transition-colors"
                  >
                    Node Father
                  </a>
                  , Copyright 2025.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;